import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Typography,
    Grid,
    Paper,
    List,
    ListItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CodeIcon from '@mui/icons-material/Code';
import SavingsIcon from '@mui/icons-material/Savings';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

function LandingPage() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleGetStarted = () => {
        navigate('/chat');
    };

    const cards = [
        { icon: AutoAwesomeIcon, title: 'Smart Model Selection', description: 'Our proprietary algorithm selects the best AI model for every query' },
        { icon: SavingsIcon, title: 'One Subscription', description: 'Access all AI models with one subscription, save hundreds of dollars' },
        { icon: CodeIcon, title: 'Versatile AI Capabilities', description: 'Legal assistance with Google Gemma, image generation with Google Nano Banana, and coding help with Claude - all in one place' },
    ];

    const faqs = [
        { question: 'How does Parley select the right AI model?', answer: 'Parley uses a proprietary algorithm that analyzes your query and automatically selects the best AI model from over 500+ available models to provide the most accurate and relevant response.' },
        { question: 'Do I need multiple subscriptions?', answer: 'No! With Parley, you only need one subscription to access all AI models. This saves you hundreds of dollars compared to subscribing to multiple AI services separately.' },
        { question: 'Can I use multiple models in the same conversation?', answer: 'Yes! Parley maintains shared context across multiple models in a single chat, allowing you to seamlessly switch between different AI capabilities without losing conversation history.' },
        { question: 'What types of queries can Parley handle?', answer: 'Parley can handle a wide variety of queries including legal assistance, image generation, coding help, general questions, and much more. The right model is automatically selected for each specific task.' },
    ];

    const fadeInAnimation = {
                        animation: 'fadeIn 0.6s ease-in',
        '@keyframes fadeIn': {
            from: { opacity: 0, transform: 'translateY(20px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
        },
    };

    return (
        <Grid
            sx={{
                background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 50%, #7dd3fc 100%)',                 display: 'flex', justifyContent: 'center', alignItems: 'center',paddingTop:7
            }}
        >
            <Grid container sx={{ width: isMobile? '100%' :'90%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {/* Hero Section */}
                <Grid container size={{xs:12,md:12}} spacing={3}
                    sx={{
                        p: 2,
                        ...fadeInAnimation,
                    }}
                >
                    {/* Left Column - Text (30%) */}
                    <Grid item size={{ xs: 12, md: 3 }}
                        sx={{
                            textAlign: { xs: 'center', sm: 'left' },
                        }}
                    >
                        <Typography
                            variant={isMobile ? 'h3' : 'h2'}
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                mb: 3,
                                color: '#1e40af',
                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            Parley
                        </Typography>
                        <Typography
                            variant={isMobile ? 'h5' : 'h4'}
                            component="h2"
                            sx={{
                                fontWeight: 600,
                                mb: 2,
                                color: '#1e3a8a',
                            }}
                        >
                            The Right Model, Every Time
                        </Typography>
                        <List sx={{ color: '#1e40af' }}>
                            <ListItem sx={{ display: 'list-item',p:0 }}>
                                <Typography variant="h6" sx={{ color: '#1e40af'}}>
                                   - Parley is the Internet's only AI chatbot where the right model always answers your query.
                                </Typography>
                            </ListItem>
                            <ListItem sx={{ display: 'list-item',p:0}}>
                                <Typography variant="h6" sx={{ color: '#1e40af' }}>
                                   - Automatic model selection among 500+ models for every query.
                                </Typography>
                            </ListItem>
                            <ListItem sx={{ display: 'list-item',p:0}}>
                                <Typography variant="h6" sx={{ color: '#1e40af' }}>
                                   - Context shared across multiple models in a single chat.
                                </Typography>
                            </ListItem>
                        </List>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleGetStarted}
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                textTransform: 'none',
                                borderRadius: '8px',
                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                '&:hover': {
                                    opacity: 0.9,
                                    transform: 'translateY(-2px)',
                                    boxShadow: 4,
                                },
                                transition: 'all 0.3s ease',
                                color:'white'
                            }}
                        >
                            Try for free
                        </Button>
                    </Grid>

                    {/* Right Column - Image (70%) */}
                    <Grid size={{ xs: 12, md: 9 }}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <img
                            src="/hero.png"
                            alt="Parley Hero"
                            style={{
                                maxWidth: '100%',
                                height: 'auto',
                                objectFit: 'contain',
                                borderRadius: '16px',
                            }}
                        />
                    </Grid>
                </Grid>

                {/* Cards Section */}
                <Grid container size={{xs:12,md:12}} spacing={3} sx={{  p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {cards.map((card, index) => {
                        const IconComponent = card.icon;
                        return (
                            <Grid size={{ xs: 12, md: 4,width: '100%' }} key={index}>
                                <Paper sx={{  p: 2,width: '100%', height: { xs: theme.spacing(30), md: theme.spacing(35) }, textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.5)', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.7)', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)' }, transition: 'all 0.3s ease', display: 'flex', flexDirection: 'column', justifyContent: 'center',
                        ...fadeInAnimation, }}>
                                    <IconComponent sx={{ fontSize: 48, color: '#3b82f6', mb: 2, mx: 'auto', display: 'block' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1e3a8a' }}>{card.title}</Typography>
                                    <Typography variant="body2" sx={{ color: '#1e40af' }}>{card.description}</Typography>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* FAQ Section */}
                <Grid container size={{xs:12,md:12}} sx={{ p: 2 }}>
                    <Grid item size={{xs:12,md:12}} sx={{ textAlign: 'center', }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 700,
                                mb: 2,
                                color: '#1e3a8a',
                            }}
                        >
                            Frequently Asked Questions
                        </Typography>
                    </Grid>
                    <Grid item size={{xs:12,md:8,
                        ...fadeInAnimation}} sx={{ mx: 'auto' }}>
                        {faqs.map((faq, index) => (
                            <Accordion
                                key={index}
                                sx={{
                                    mb: 2,
                                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                    borderRadius: '8px',
                                    '&:before': { display: 'none' },
                                    boxShadow: 'none',
                                    border: '1px solid rgba(255, 255, 255, 0.7)',

                        ...fadeInAnimation,
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: '#3b82f6' }} />}
                                    sx={{
                                        '& .MuiAccordionSummary-content': {
                                            my: 2,
                                        },
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e3a8a' }}>
                                        {faq.question}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant="body1" sx={{ color: '#1e40af', lineHeight: 1.6 }}>
                                        {faq.answer}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Grid>
                </Grid>

                {/* Final CTA Section */}
                <Grid container size={{xs:12,md:12}}
                    sx={{
                        p: 2,
                        ...fadeInAnimation,
                    }}
                >
                    <Grid item size={{xs:12,md:12}} sx={{ textAlign: 'center' }}>
                        <ChatBubbleOutlineIcon
                            sx={{
                                fontSize: 64,
                                color: '#3b82f6',
                                mb: 3,
                            }}
                        />
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                mb: 2,
                                color: '#1e3a8a',
                            }}
                        >
                            Ready to Experience the Future of AI Chat?
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#1e40af',
                                mb: 4,
                                maxWidth: '600px',
                                mx: 'auto',
                            }}
                        >
                            Join Parley today and let the right AI model handle every query, every time.
                            No more switching between platforms. No more wasted subscriptions.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleGetStarted}
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                textTransform: 'none',
                                borderRadius: '8px',
                                background: theme.palette.mode === 'dark'
                                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                '&:hover': {
                                    opacity: 0.9,
                                    transform: 'translateY(-2px)',
                                    boxShadow: 4,
                                },
                                transition: 'all 0.3s ease',
                                color:'white'
                            }}
                        >
                            Try for free
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default LandingPage;

